import {Autocomplete, Divider, Grid, Stack, TextField} from "@mui/material"
import {Fragment, SyntheticEvent, useEffect, useState} from "react";
import DialogNewAfiliation from "./DialogNewAfiliation";
import ShareholdersDataGrid from "./ShareholdersDataGrid";
import toast from "react-hot-toast";
import CoOwnersDataGrid, {TempCoOwnersDataGrid} from "./CoOwnersDataGrid";
import DialogNewCoOwner from "./DialogNewCoOwner";
import {BotonAgregar} from "../../../components/Buttons";
import {
    Client, ClientClassificationTypes,
    ClientFields,
    ClientRelationshipView,
    ClientRelationshipViewFields,
    ClientType,
    CoOwnerRelationshipWithoutSolicitation
} from "src/types/client/clientData";
import {
    SolicitationClient,
    SolicitationClientFields,
    SolicitationFields
} from "../../../../../types/solicitation/solicitationData";
import {HttpSolicitation, HttpSolicitationClient} from "../../../../../http/solicitation/httpSolicitation";
import DeleteDialog from "../../../../../components/dialog/DeleteDialog";
import {LoaderBlockUI} from "../../../../../components/loader";
import {EntityWithIdFields} from "../../../../../types/baseEntities";
import {HttpClientRelationships} from "../../../../../http/client/httpClient";
import {FontSubtitleTwo} from "../../../components/SharedTypography";
import {ClientRelationshipClassificationCodes} from "../../../../../types/client/clientEnums";


interface SuretiesShareholdingStructureProps {
    client: Client,
    coOwnersFront: CoOwnerRelationshipWithoutSolicitation[],
    setCoOwnersFront: (list: CoOwnerRelationshipWithoutSolicitation[]) => void,
    setCoOwnersCheck: (arg: boolean) => void,
    solicitationId?: number
}


const TEXTOS = {
    COMPOSICION_ACCIONARIA: "Composición Accionaria",
    COMPOSICION_SOCIETARIA: "Composición Societaria",
    ACCIONARIOS_INFO: "Datos filiatorios de accionistas o socios, quienes en conjunto concentran el 100% de las acciones.",
    COMPOSICION_COOPERATIVA_INFO: "Se requieren por lo menos los datos del presidente, secretario, tesorero y hasta 6 socios de la empresa."
}


const SuretiesShareholdingStructure = ({client, coOwnersFront, setCoOwnersFront, setCoOwnersCheck, solicitationId}: SuretiesShareholdingStructureProps) => {
    const [selectedClientRelationshipList, setSelectedClientRelationshipList] = useState<ClientRelationshipView[]>([])
    const [clientRelationships, setClientRelationships] = useState<ClientRelationshipView[]>([])
    const [clientToEdit, setClientToEdit] = useState<ClientRelationshipView | undefined>(undefined)
    const [open, setOpen] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const [coOwnersBack, setCoOwnersBack] = useState<SolicitationClient[]>()
    const [prevCoOwner, setPrevCoOwner] = useState<SolicitationClient | undefined>(undefined)
    const [prevCoOwnerFront, setPrevCoOwnerFront] = useState<CoOwnerRelationshipWithoutSolicitation | undefined>(undefined)
    const [coOwnerDeleteId, setCoOwnerDeleteId] = useState<number>(0)
    const [clientDeleteId, setClientDeleteId] = useState<number>(0)
    const [openDelete, setOpenDelete] = useState<boolean>(false)
    const [hasCoOwner, setHasCoOwner] = useState<string | null>('No')
    const [openDialog, setOpenDialog] = useState<boolean>(false)
    const isLegalPerson = client[ClientFields.ClientTypeCode] === ClientType.Juridica
    
    const loadClientRelationships = () => {
        HttpClientRelationships.getPorIdCliente(client[EntityWithIdFields.Id]).then((r) => setSelectedClientRelationshipList(r))
    }
    
    useEffect(() => {
        loadClientRelationships()
    }, [client])

    const loadClientCoOwners = () => {
        if (solicitationId) {
            setLoading(true)
            HttpSolicitation.getClientRelatedsByIdSolicitation(solicitationId).then((r) => {
                const coOwners = r.filter((x) => x[SolicitationClientFields.RelationshipClassificationCode] === ClientRelationshipClassificationCodes.CoOwners)
                setCoOwnersBack(coOwners)
                setHasCoOwner(coOwners.length > 0 ? 'Si' : 'No')
                coOwners.length !== 0 && setCoOwnersCheck(true)
            }).finally(() => setLoading(false))
        }
    }


    useEffect(() => {
        solicitationId !== 0 && loadClientCoOwners()
    }, [solicitationId])
    
    const onEdit = (client: ClientRelationshipView) => {
        setClientToEdit(client)
        setClientRelationships(selectedClientRelationshipList.filter((relationship) => relationship[ClientRelationshipViewFields.ClientId] === client[ClientRelationshipViewFields.ClientId]))
        setOpen(true)
    }
    
    
    const onDelete = () => {
        setLoading(true)
        HttpClientRelationships.delete(client[EntityWithIdFields.Id], clientDeleteId).then(() => {
            loadClientRelationships()
        }).catch(() => {
            toast.error("Ocurrió un error al eliminar cliente")
        }).finally(() => {
            setLoading(false)
        })
    }
    
    const onEditCoOwnerBack = (coOwner: SolicitationClient) => {
        setPrevCoOwner(coOwner)
        setOpenDialog(true)
    }
    
    const onDeleteCoOwnerBack = () => {
        if (solicitationId) {
            setLoading(true)
            HttpSolicitationClient.deleteClientRelated(solicitationId, coOwnerDeleteId).then(() => {
                setLoading(false)
                loadClientCoOwners()
            }).catch(() => {
                toast.error("Ocurrió un error al eliminar el cotitular")
            }).finally(() => {
                setLoading(false)
            })
        }
    }
    
    const onEditCoOwnerFront = (coOwner: CoOwnerRelationshipWithoutSolicitation) => {
        setPrevCoOwnerFront(coOwner)
        setOpenDialog(true)
    }

    const onDeleteCoOwnerFront = () => {
        const newList = coOwnersFront.filter((x) => x[EntityWithIdFields.Id] !== coOwnerDeleteId)
        setCoOwnersFront(newList)
        newList.length == 0 && setCoOwnersCheck(false)
    }
    
    const onDeleteCoOwner = (id: number) => {
        setOpenDelete(true)
        setCoOwnerDeleteId(id)
    }
    
    const onDeleteClient = (id: number) => {
        setOpenDelete(true)
        setClientDeleteId(id)
    }
    const getCoOwnersDataGrid = () => {
        if (solicitationId && solicitationId !== 0) {
            return <CoOwnersDataGrid relationships={coOwnersBack} onEdit={onEditCoOwnerBack} onDelete={onDeleteCoOwner}/>
        }else{
            return <TempCoOwnersDataGrid relationships={coOwnersFront} onEdit={onEditCoOwnerFront} onDelete={onDeleteCoOwner}/>
        }
    }
    
    const getTotalParticipation = () => {
        if (solicitationId !== 0) {
            return coOwnersBack?.reduce((acc, c) => acc + c[SolicitationClientFields.Participation], 0) || 0;
        } else {
            return coOwnersFront.reduce((acc, c) => acc + c[ClientRelationshipViewFields.Participation], 0);
        }
    }

    const handleInputChange = (event: SyntheticEvent<Element, Event>, newValue: string | null) => {
        setHasCoOwner(newValue);
        
        if ((coOwnersBack && coOwnersBack.length !== 0) && newValue === 'Si') setCoOwnersCheck(true)
        if (!coOwnersBack && coOwnersFront.length !== 0 && newValue === 'Si') setCoOwnersCheck(true)
        if (newValue === 'Si' && coOwnersBack && coOwnersBack.length == 0) {
            setCoOwnersCheck(false)
        }
        if (newValue === 'Si' && !coOwnersBack && coOwnersFront.length == 0){
            setCoOwnersCheck(false)
        }
        if (newValue === 'No') {
            setCoOwnersBack(undefined)
            setCoOwnersFront([])
            setCoOwnersCheck(true)
        }
    }

    const onSetCoOwnersFront = (lst: CoOwnerRelationshipWithoutSolicitation[]) => {
        setCoOwnersFront(lst)
        setCoOwnersCheck(true)
    }
    
    return (
        <Fragment>
                {
                    !loading && (
                <Stack spacing={2} mt={1}>
                    {isLegalPerson &&
                        <Fragment>
                            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                                <FontSubtitleTwo text={client[ClientFields.ClientClassificationCode] === ClientClassificationTypes.SA ?
                                    TEXTOS.COMPOSICION_ACCIONARIA
                                    : TEXTOS.COMPOSICION_SOCIETARIA} />
                                <BotonAgregar onClick={() => setOpen(true)} />
                            </Stack>
                            <Divider sx={{ mb: 2 }} />
                            <ShareholdersDataGrid relationships={selectedClientRelationshipList} onEdit={onEdit} onDelete={onDeleteClient}/>
                        </Fragment>
                    }
                <Grid container alignItems='center' spacing={4} mt={2} mb={3}>
                    <Grid item xs={6} md={1.5}>
                        <Autocomplete
                            options={['Si', 'No']}
                            onChange={handleInputChange}
                            defaultValue={hasCoOwner}
                            renderInput={(params) =>
                                <TextField
                                    {...params}
                                    label="¿Tiene cotitular?"
                                    name={SolicitationFields.HasCoOwner}
                                    size='small'
                                />
                            }
                            size='small'
                        />
                    </Grid>
                </Grid>
                {
                    (hasCoOwner === 'Si') && 
                    <Stack pt={12}>
                        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                            <FontSubtitleTwo text='Co-titulares' />
                            { (client[ClientFields.ClientTypeCode] === ClientType.Juridica && getTotalParticipation() <= 100) && <BotonAgregar onClick={() => setOpenDialog(true)} /> }
                            { client[ClientFields.ClientTypeCode] === ClientType.Fisica && <BotonAgregar onClick={() => setOpenDialog(true)}/>}
                        </Stack>
                        <Divider/>
                        {
                            getCoOwnersDataGrid()
                        }
                    </Stack>
                }
            </Stack>
                    )}

            <DialogNewAfiliation
                open={open}
                onNewAffiliated={() => {
                    loadClientRelationships()
                    setOpen(false)
                    setClientToEdit(undefined)
                }}
                onClose={() => {
                    setClientToEdit(undefined)
                    setOpen(false)
                    setClientRelationships([])
                }}
                onReload={loadClientRelationships}
                clientId={client[EntityWithIdFields.Id]}
                clientToEdit={clientToEdit}
                solicitationId={solicitationId}
                clientRelationships={clientRelationships}
                clientClassificationCode={client[ClientFields.ClientClassificationCode]}
            />
            
            {
                clientDeleteId !== 0 &&
                <DeleteDialog open={openDelete}
                              onClose={() => {setOpenDelete(false)}}
                              onDelete={onDelete}
                              title={'Eliminar cliente'}
                              description={'¿Está seguro que desea eliminar el cliente?'}
                />
            }
            {
                coOwnerDeleteId !== 0 &&
                <DeleteDialog open={openDelete}
                              onClose={() => {setOpenDelete(false)}}
                              onDelete={solicitationId !== 0 ? onDeleteCoOwnerBack : onDeleteCoOwnerFront}
                              title={'Eliminar cotitular'}
                              description={'¿Está seguro que desea eliminar el cotitular?'}
                />
            }
                          
            {
                solicitationId && solicitationId !== 0 ?
                    <DialogNewCoOwner open={openDialog}
                                      onClose={() => {
                                          setOpenDialog(false)
                                          setPrevCoOwner(undefined)
                                      }}
                                      prevCoOwner={prevCoOwner}
                                      onFinishBackSave={loadClientCoOwners}
                    />
                    : 
                    <DialogNewCoOwner open={openDialog}
                                      onClose={() => {
                                          setOpenDialog(false)
                                          setPrevCoOwnerFront(undefined)
                                      }}
                                      prevCoOwnerFront={prevCoOwnerFront}
                                      setCoOwners={onSetCoOwnersFront}
                                      coOwnersFront={coOwnersFront}
                                      withoutSolicitation
                    />
            }
            {loading && <LoaderBlockUI/>}
        </Fragment>
    )
}

export default SuretiesShareholdingStructure